import { isMapEditing, mapEditRevision } from './editlock';
// The selection card: click any object and its instruments appear —
// attributes, relations (clickable), history, actions. One card, bottom-left.

import * as maplibregl from 'maplibre-gl';
import type { EntityDto, Me } from './api';
import { del, get, post, put } from './api';
import { getStories, getStory, orderedStories } from './story';

const esc = (s: string) =>
  s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!));
const escAttr = (s: string) =>
  s.replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

// "how long ago" for an event, leaning on hours as the user reads these cards.
function agoPhrase(atMs: number): string {
  const s = (Date.now() - atMs) / 1000;
  if (!Number.isFinite(s) || s < 0) return '';
  if (s < 60) return 'just now';
  if (s < 3600) { const m = Math.round(s / 60); return `${m} min ago`; }
  if (s < 172_800) { const h = Math.round(s / 3600); return `${h} hour${h === 1 ? '' : 's'} ago`; }
  const d = Math.round(s / 86_400);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

// Provenance/system attrs the edit form should not surface as free-form fields
// (they are set by the pipeline, not the operator). HIDDEN_ATTRS covers the
// rest (raw event payload, location resolution).
const EDIT_SYSTEM = new Set(['evidence_count', 'needs_placement', 'approximate',
                             'sited_from', 'moved_km', 'sourceCount', 'confidence',
                             'basis']);

// Universal flags that live in attrs but bypass any type's schema (mirrors the
// backend's RESERVED_ATTR_KEYS) — so they survive a switch to a strict target.
const RESERVED_FLAGS = new Set(['unconfirmed', 'confirmed', 'suspected']);

// The coarse buckets a type's category belongs to, in the order the picker
// groups them. Labels are the UI's domain names (see registry `domain`).
const DOMAIN_LABEL: Record<string, string> = {
  place: 'Places', object: 'Objects', event: 'Events', actor: 'Actors',
};
import {
  cacheEntities, cachedEntity, cachedEntitiesOfType, getRegistry,
  radarRangeKmOf, radarShapeOf,
  rangeKmOf, rangeRingFeatures, setCoverageExclusion, setSelected,
} from './objects';
import { eventsNear } from './corroborate';
import { domesVisible } from './domes';
import { hideRotateHandle, showRotateHandle } from './rotate';
import { channelLabel, LIVE_KINDS, sourceLinks } from './livekinds';
import { CARD_PX, mountMedia } from './media';
import { reliabilityOf, treatmentOf } from './channelmeta';
import { fmtAbs, relTimeMs, whenOf } from './when';
import { startReassignFromCard, startResolveFromCard } from './territory';

// The marker types that can DOCUMENT an event (migration 037): the link is
// authored from the marker side — write access to the marker is the bar —
// and the event side renders it read-only.
const MARKER_TYPES = new Set(['damage_marker', 'observation']);

// --- map-pick mode -----------------------------------------------------------
// While a card's "pick on the map" flow is armed, feature clicks are consumed
// here INSTEAD of opening a selection card — main.ts asks first. A dropdown of
// a hundred rows all named "Damage marker" is not a picker; the map is.
let mapPick: { accept: (id: string) => void } | null = null;

/** main.ts calls this from its feature-click handler before opening a card.
 *  True = the click belonged to an armed pick flow and is consumed. */
export function tryConsumeMapPick(id: string): boolean {
  if (!mapPick) return false;
  mapPick.accept(id);
  return true;
}

/** Whether a pick/place flow is armed — main.ts's empty-map-click handler
 *  must not close the card (and with it the flow) over a missed click. */
export function mapPickActive(): boolean {
  return mapPick !== null;
}

/** Arm the pick mode from OUTSIDE this module (the group card runs its own
 *  place-a-marker flow). Returns the release. An accept of () => {} is the
 *  swallow-everything used while placing a NEW point, so feature clicks
 *  cannot hijack the flow into opening cards. */
export function armMapPick(accept: (id: string) => void): () => void {
  const mode = { accept };
  mapPick = mode;
  return () => {
    if (mapPick === mode) mapPick = null;
  };
}

interface HistoryVersion {
  rev: number; type: string; name: string | null; validFrom: string;
  validTo: string | null; changeKind: string; byRule: boolean;
  createdBy: string | null;
}

// bookmark/bookmark_id are the pre-008 attach keys; place_* and admin* are
// their successors — all rendered as the location line, not raw attr rows.
const HIDDEN_ATTRS = new Set(['legacy_id', 'incident_key', 'text', 'sources',
                              'postId', 'postUrl', 'seq', 'bookmark_id',
                              'bookmark', 'place_id', 'place_name',
                              'location_kind', 'admin0', 'admin0_iso', 'admin1',
                              // shown as a badge / edit checkbox, not an attr row
                              'unconfirmed', 'confirmed', 'suspected',
                              // rendered as its own anchors block, and edited
                              // through the schema-declared array field
                              'links']);

// The geometry kinds the on-map draw tools can lay down (line/none aren't
// drawable yet), so the only kinds the editor offers to (re)draw.
export type GeometryEditKind = 'point' | 'polygon';

export interface SelectionHooks {
  onScanHere: (entity: EntityDto) => void;
  onDeleted: () => void;
  onOpenPeer: (id: string) => void;
  onEdited: () => void;
  // Geometry editing needs the map draw tools, which live in main.ts — the
  // card hands the entity back and main runs the redraw/move, then reopens.
  // `kind` is the geometry the operator asked to lay down, which may differ
  // from the current one (a point base gaining a polygon outline, say) — the
  // draw tools replace the geometry wholesale, so a kind switch just works.
  onEditGeometry: (entity: EntityDto, kind: GeometryEditKind) => void;
  // A territory's boundary is DRAGGED, not redrawn: the outline is a
  // country's worth of vertices and a redraw would replace it with a
  // hand-clicked ring. main.ts owns the editor (territoryedit.ts) for the
  // same reason it owns the draw tools.
  onDragBoundary?: (entity: EntityDto) => void;
  // Draw a line across the territory; one side of it changes hands.
  onCutBoundary?: (entity: EntityDto) => void;
  // Mark an area of ground contested or held: draw it, paint it, or pick
  // admin units (territoryarea.ts through territory.ts's form).
  onMarkArea?: (entity: EntityDto) => void;
  // Draw around ground and it joins THIS territory (territory.ts's Claim
  // area with the card's slot preset).
  onAddGround?: (entity: EntityDto) => void;
}

let host: HTMLElement | null = null;

// Whether this session may curate stories (today: admin — app/auth/deps.py's
// require_curator). The card has no `me`: main.ts owns it, and widening
// openSelection's signature would reach into files this change does not touch.
// So probe /api/me once, on the first card that needs the answer, and reuse it
// for the session. The server refuses the write regardless; hiding the control
// only keeps a dead affordance off the card.
let curator: boolean | null = null;
let curatorProbe: Promise<boolean> | null = null;
function isCurator(): Promise<boolean> {
  if (curator !== null) return Promise.resolve(curator);
  curatorProbe ??= get<Me>('/api/me')
    .then((m) => (curator = m.isAdmin))
    .catch(() => (curator = false));
  return curatorProbe;
}

// "One bottom-left card at a time" must hold in BOTH directions: the group
// card (panels.ts) closes the selection card when it opens, and registers its
// own close here so opening a selection closes it back. A callback rather than
// an import — panels.ts imports this module, so the reverse edge would be a
// cycle.
let companionCardClose: (() => void) | null = null;
export function setCompanionCardClose(cb: () => void): void {
  companionCardClose = cb;
}

/** The object whose card is open, if any. */
export function selectedEntityId(): string | null {
  return host?.dataset.entityId ?? null;
}

export function closeSelection(map: maplibregl.Map): void {
  host?.remove();
  host = null;
  repaintDocs = null;
  mapPick = null;                    // an armed pick must not outlive its card
  map.getCanvas().style.cursor = '';
  setSelected(map, []);
  setCoverageExclusion(map, null);   // the unit rejoins the reference-ring layer
  hideRotateHandle(map);
}

export async function openSelection(map: maplibregl.Map, id: string,
                                    hooks: SelectionHooks): Promise<void> {
  if (isMapEditing()) return;
  // Instant path: if we already hold this entity (any pane load / live feed),
  // paint the card now and revalidate in the background — the fetch only adds
  // relations and catches a concurrent edit. Falls back to fetch-first when the
  // entity isn't cached (e.g. a distant object outside the loaded set).
  const cached = cachedEntity(id);
  if (cached) {
    renderCard(map, cached, hooks);
    // Revalidate for the one thing the list-loaded entity lacks — relations —
    // and patch just that node so the open card's state (range toggle, history)
    // survives. If the card has since changed or the fetch fails, do nothing.
    void get<EntityDto>(`/api/entities/${id}`).then((fresh) => {
      cacheEntities([fresh]);
      if (host?.dataset.entityId === id) patchRelations(fresh, hooks);
    }).catch(() => { /* keep the cached card */ });
    return;
  }
  const editRevision = mapEditRevision();
  const entity = await get<EntityDto>(`/api/entities/${id}`);
  if (editRevision !== mapEditRevision()) return;
  cacheEntities([entity]);
  renderCard(map, entity, hooks);
}

// Set by renderCard so the background revalidate (which is what actually
// delivers the edges) can repaint the documents section too, not just the
// generic relations list. Cleared with the card.
let repaintDocs: ((entity: EntityDto) => void) | null = null;

function patchRelations(entity: EntityDto, hooks: SelectionHooks): void {
  const box = host?.querySelector('#sel-relations');
  if (!box) return;   // absent while the editor is open — nothing to patch
  box.innerHTML = (entity.edges ?? [])
    .filter((e) => e.type !== 'documents')   // rendered by the docs section
    .map((e) =>
      `<div class="rel" data-peer="${e.peerId}">${e.label} ` +
      `${e.direction === 'out' ? '→' : '←'} ${e.peerName ?? e.peerType}</div>`).join('');
  box.querySelectorAll<HTMLElement>('.rel').forEach((r) =>
    r.addEventListener('click', () => hooks.onOpenPeer(r.dataset.peer!)));
  repaintDocs?.(entity);
}

function renderCard(map: maplibregl.Map, entity: EntityDto,
                    hooks: SelectionHooks): void {
  if (isMapEditing()) return;
  companionCardClose?.();   // one bottom-left card at a time, both directions
  const registry = getRegistry();
  const type = registry?.types.find((t) => t.key === entity.type);
  const selColor = type?.style.color ?? '#55d6be';
  const rangeKm = rangeKmOf(entity);
  const radarKm = radarRangeKmOf(entity);
  // Don't stack flat 2D rings on a unit whose coverage is already drawn as a 3D
  // volume — clicking the volume would otherwise light up redundant ground rings.
  // The card's toggle still lets the operator turn them on.
  let showRange = (rangeKm !== null || radarKm !== null) && !domesVisible();
  const applySelection = () => {
    // Same builder as the always-on reference layer (rangeRingFeatures), so the
    // card's rings and the layers-panel rings are one geometry, never two.
    setSelected(map, [entity], showRange ? rangeRingFeatures(entity) : []);
    // Drop this unit from the always-on reference layer only while the card is
    // actually drawing its ring — so the two never double up, and a unit whose
    // card ring is off (e.g. 3D volumes are showing instead) still keeps its
    // reference ring like every other unit.
    setCoverageExclusion(map, showRange ? entity.id : null);
  };
  applySelection();

  // The rotate grip is an editing affordance: it appears with the editor
  // (renderEditor below), never on a plain selection.
  hideRotateHandle(map);
  host?.remove();
  host = document.createElement('div');
  host.className = `pod selcard sel-${entity.category}`;
  host.dataset.entityId = entity.id;   // lets a background revalidate re-render
  host.style.setProperty('--sel-color', selColor);
  const altitude = typeof entity.attrs.altitude_m === 'number'
    ? entity.attrs.altitude_m as number : null;
  const attrs = Object.entries(entity.attrs)
    .filter(([k, v]) => v !== null && v !== '' && !HIDDEN_ATTRS.has(k))
    .slice(0, 10);
  // Empty for anything without folded sources or a post url (i.e. non-events).
  const sources = sourceLinks(entity.attrs);
  // Operator-attached URLs (attrs.links, 036) — supporting material on a
  // damage report or anything else whose schema declares them. http(s) only:
  // these render as anchors, so anything else is a payload, not a link.
  const attachedLinks = (Array.isArray(entity.attrs.links) ? entity.attrs.links : [])
    .map((u) => String(u)).filter((u) => /^https?:\/\//i.test(u));
  const linkLabel = (u: string): string => {
    try { return new URL(u).hostname.replace(/^www\./, ''); }
    catch { return u.slice(0, 40); }
  };

  const rangeParts: string[] = [];
  if (rangeKm !== null) rangeParts.push(`${rangeKm.toLocaleString()} km effective`);
  if (radarKm !== null) rangeParts.push(`${radarKm.toLocaleString()} km radar`);

  // Events say where they happened, in resolution order: at a mapped place,
  // in an admin area, or nothing extra (the geocoded place attr row stands).
  let locationLine = '';
  const nearName = String(entity.attrs.place_name ?? entity.attrs.bookmark ?? '');
  if (nearName) {
    locationLine = `at ${nearName}`;
  } else if (entity.attrs.location_kind === 'admin') {
    const admin1 = String(entity.attrs.admin1 ?? '');
    locationLine = `in ${admin1 ? `${admin1}, ` : ''}${String(entity.attrs.admin0 ?? '')}`;
  }

  // Events carry a "how long ago" line, through the shared occurrence clock
  // rather than a fourth preference order of its own. The absolute time and the
  // basis go in the tooltip: this card is where someone comes to check a claim,
  // and a relative time with no wall-clock anywhere is not checkable.
  const isEvent = entity.category === 'event';
  const eventWhen = whenOf(entity);
  const whenLine = isEvent ? agoPhrase(eventWhen.at) : '';
  const whenTitle = isEvent
    ? `${fmtAbs(eventWhen.at)}${eventWhen.basis === 'row'
        ? ' — no reported time; this is when we recorded it' : ''}`
    : '';

  host.innerHTML = `
    <header>
      <span class="sel-dot"></span>
      <div style="flex:1;min-width:0">
        <div class="type">${type?.label ?? entity.type}</div>
        <h4>${entity.name ?? '(unnamed)'}</h4>
        ${whenLine ? `<div class="sel-when" title="${escAttr(whenTitle)}">${
          whenLine}</div>` : ''}
        ${locationLine ? `<div class="sel-where">${locationLine}</div>` : ''}
      </div>
      ${entity.attrs.unconfirmed ? '<span class="sel-status status-unconfirmed">unconfirmed</span>' : ''}
      ${entity.attrs.status ? `<span class="sel-status status-${entity.attrs.status}">${entity.attrs.status}</span>` : ''}
      <span class="x" title="Close">✕</span>
    </header>
    <div class="body">
      <div class="sel-media card-media"></div>
      <dl>${attrs.map(([k, v]) =>
        `<dt>${k}</dt><dd>${String(v).slice(0, 160)}</dd>`).join('')}
      ${sources.length ? `<dt>sources</dt><dd class="sel-sources">${sources.map((s) => {
        const kept = Array.isArray(entity.attrs.links)
          && (entity.attrs.links as unknown[]).map(String).includes(s.url);
        const tipParts = [
          ...(reliabilityOf(s.channel) && reliabilityOf(s.channel) !== 'standard'
            ? [`${reliabilityOf(s.channel)} reliability`] : []),
          ...(treatmentOf(s.channel) ? [treatmentOf(s.channel)!] : []),
        ];
        const tip = tipParts.length
          ? `${channelLabel(s.channel) || 'post'} — ${tipParts.join(' · ')}`
          : 'Open source post';
        return `<a href="${escAttr(s.url)}" target="_blank" rel="noopener"
          title="${escAttr(tip)}">${
          esc(channelLabel(s.channel) || 'post')} ↗</a>${
          entity.type === 'live_event'
            ? (kept
              ? '<span class="src-kept" title="Already attached as a curated link">✓</span>'
              : `<button type="button" class="src-keep" data-url="${escAttr(s.url)}"
                  title="Attach this post as a curated link on this event — one click">＋</button>`)
            : ''}`;
      }).join('')}</dd>` : ''}
      ${attachedLinks.length ? `<dt>links</dt><dd class="sel-sources">${attachedLinks.map((u) =>
        `<a href="${escAttr(u)}" target="_blank" rel="noopener noreferrer" title="${escAttr(u)}">${
          esc(linkLabel(u))} ↗</a>`).join('')}</dd>` : ''}
      ${altitude !== null ? `<dt>altitude</dt><dd>${altitude.toLocaleString()} m AGL</dd>` : ''}</dl>
      ${MARKER_TYPES.has(entity.type) || entity.type === 'live_event'
        ? '<div id="sel-addlink"></div>' : ''}
      ${rangeParts.length ? `<label class="range-toggle">
        <input type="checkbox" id="sel-range"${showRange ? ' checked' : ''} /><i></i>
        <span><b>Show range rings</b><small>${rangeParts.join(' · ')} (nominal)</small></span>
        </label>` : ''}
      <div id="sel-story"></div>
      <div id="sel-relations">${(entity.edges ?? [])
        .filter((e) => e.type !== 'documents')
        .map((e) =>
        `<div class="rel" data-peer="${e.peerId}">${e.label} ${e.direction === 'out' ? '→' : '←'} ${e.peerName ?? e.peerType}</div>`).join('')}</div>
      <div id="sel-docs"></div>
      <div id="sel-history"></div>
    </div>
    <div class="actions">
      ${entity.type === 'territory'
        // Contested ground's first question is who holds it NOW — the whole
        // territory is captured (or re-labelled) in one step; a controlled
        // territory's is which district moved.
        ? (entity.attrs.status === 'contested'
            ? '<button class="primary" id="sel-resolve" title="Say who holds this contested ground now — the whole area changes hands">⚑ Captured by…</button>'
              + '<button id="sel-reassign" title="Click a district on the map and say who holds it now — keep painting for a run of them">▦ Reassign district</button>'
            : '<button class="primary" id="sel-reassign" title="Click a district on the map and say who holds it now — keep painting for a run of them">▦ Reassign district</button>'
              + '<button id="sel-resolve" title="Hand the whole territory over, or mark all of it contested">⚑ Whole territory…</button>')
          + '<button id="sel-claim" hidden title="Draw a line around ground; it joins this territory">✎ Add ground</button>'
          + '<button id="sel-drag" hidden title="Drag the vertices of the country\'s boundaries; a shared line moves on both sides, and the ground across it changes hands">⤢ Reshape boundaries</button>'
          + '<button id="sel-cut" hidden title="Draw a line across this territory; one side of it changes hands">✂ Split territory</button>'
          + '<button id="sel-area" hidden title="Draw, paint or pick admin units to mark ground as contested or held">◩ Mark contested area</button>'
        : '<button class="primary" id="sel-scan">Scan imagery</button>'}
      <button id="sel-edit">Edit</button>
      <button id="sel-history-btn">History</button>
      <button id="sel-zoom">Zoom</button>
      <button id="sel-delete">Delete</button>
    </div>`;

  document.querySelector('.cockpit')!.appendChild(host);
  // The pictures those posts carry, one per picture, first thing in the body.
  mountMedia(host.querySelector('.sel-media') as HTMLElement, `ent:${entity.id}`,
             [entity], { max: 6, px: CARD_PX });
  host.querySelector('.x')!.addEventListener('click', () => closeSelection(map));

  host.querySelector('#sel-range')?.addEventListener('change', (e) => {
    showRange = (e.target as HTMLInputElement).checked;
    applySelection();
  });

  host.querySelectorAll<HTMLElement>('.rel').forEach((r) =>
    r.addEventListener('click', () => hooks.onOpenPeer(r.dataset.peer!)));

  paintStory();
  paintDocs(entity);
  repaintDocs = paintDocs;
  paintAddLink();

  // One-click promotion: a source post URL becomes a curated link. The same
  // write as the ＋ Add link field, minus the typing.
  host.querySelectorAll<HTMLButtonElement>('.src-keep').forEach((b) =>
    b.addEventListener('click', () => {
      void (async () => {
        const url = b.dataset.url!;
        const existing = Array.isArray(entity.attrs.links)
          ? entity.attrs.links.map(String) : [];
        if (existing.includes(url)) return;
        try {
          await put(`/api/entities/${entity.id}`,
                    { attrs: { ...entity.attrs, links: [...existing, url] } });
          hooks.onEdited();
          await reloadCard();
        } catch { b.textContent = '✗'; }
      })();
    }));

  // Attach a URL without entering the full editor — the common gesture is
  // "here's the video", one paste. PUT replaces attrs wholesale, so the whole
  // object is sent back with links appended; a live_event's pipeline attrs
  // ride along unchanged (its schema is open), and the marker schemas
  // declare links so validation holds.
  function paintAddLink(): void {
    const box = host?.querySelector('#sel-addlink');
    if (!box) return;
    const idle = (): void => {
      box.innerHTML = '<button type="button" class="mini">＋ Add link</button>';
      box.querySelector('button')!.addEventListener('click', editing);
    };
    const editing = (): void => {
      box.innerHTML = `
        <input type="url" placeholder="https://… (video, post, article)" />
        <button type="button" class="mini" data-a="save">Save</button>
        <button type="button" class="mini" data-a="cancel">Cancel</button>`;
      const input = box.querySelector('input')!;
      input.focus();
      box.querySelector('[data-a="cancel"]')!.addEventListener('click', idle);
      box.querySelector('[data-a="save"]')!.addEventListener('click', () => {
        void (async () => {
          const url = input.value.trim();
          if (!/^https?:\/\//i.test(url)) return;   // anchors only, 036's rule
          const existing = Array.isArray(entity.attrs.links)
            ? entity.attrs.links.map(String) : [];
          if (existing.includes(url)) { idle(); return; }
          try {
            await put(`/api/entities/${entity.id}`,
                      { attrs: { ...entity.attrs, links: [...existing, url] } });
            hooks.onEdited();
            await reloadCard();
          } catch {
            box.innerHTML = '<small>Could not save the link (write access?).</small>';
          }
        })();
      });
    };
    idle();
  }

  // --- "documents" — what this marker is evidence FOR (migration 037) -------
  // The source URL lives ONCE, on the marker (attrs.links); the edge is what
  // lets every event it documents reach it without the URL being copied
  // around. Authored from the marker side only; an event shows its markers
  // read-only, and clicking through lands on the marker with its links.

  function pointOfAny(e: EntityDto): [number, number] | null {
    const g = e.geometry;
    if (!g) return null;
    if (g.type === 'Point') return g.coordinates as [number, number];
    if (g.type === 'Polygon') {
      const ring = (g.coordinates as [number, number][][])[0];
      return ring?.[0] ?? null;
    }
    return null;
  }

  function kmBetween(a: [number, number], b: [number, number]): number {
    const rad = Math.PI / 180;
    const dLat = (b[1] - a[1]) * rad;
    const dLon = (b[0] - a[0]) * rad;
    const s = Math.sin(dLat / 2) ** 2
      + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  async function reloadCard(): Promise<void> {
    try {
      const fresh = await get<EntityDto>(`/api/entities/${entity.id}`);
      cacheEntities([fresh]);
      renderCard(map, fresh, hooks);
    } catch { /* keep the card as it stands */ }
  }

  function paintDocs(current: EntityDto): void {
    const box = host?.querySelector('#sel-docs');
    if (!box) return;
    const docs = (current.edges ?? []).filter((e) => e.type === 'documents');
    const isMarker = MARKER_TYPES.has(current.type);
    const isEvent = current.type === 'live_event';
    if (!docs.length && !isMarker && !isEvent) { box.innerHTML = ''; return; }
    // The unlink ✕ renders on both sides — the SERVER's bar is what holds
    // (write access to the marker); hiding it here would only pretend the
    // event side is read-only when the same person usually owns both.
    const rows = docs.map((e) => `
      <div class="rel sel-doc" data-peer="${escAttr(e.peerId)}">📎 ${
        e.direction === 'out' ? 'evidence for' : 'documented by'} ${
        esc((e.peerName ?? e.peerType).slice(0, 60))}${
        isMarker || isEvent
          ? ` <button type="button" class="doc-x" data-edge="${escAttr(e.id)}"
              title="Unlink — ends the relation; nothing is deleted">✕</button>`
          : ''}</div>`).join('');
    // The mirror of the feed's "✔ damage" chip: the chip is a COMPUTED match
    // (corroborate.damageFor), not a stored edge, so a marker beside an event
    // claiming corroboration used to show nothing — which reads as a broken
    // link. Same matcher from the other end (eventsNear), rendered as
    // suggestions with a one-click Link that authors the real edge. When the
    // cache holds nothing, nothing renders — absence of a row is not absence
    // of a nearby report (the entity list is capped).
    const linked = new Set(docs.map((e) => e.peerId));
    const nearby = isMarker
      ? eventsNear(current, cachedEntitiesOfType('live_event'))
          .filter((n) => !linked.has(n.event.id))
      : [];
    const dayOf = (at: number) => new Date(at)
      .toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    const distOf = (m: number) =>
      m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
    const nearRows = nearby.map((n) => `
      <div class="rel sel-doc sel-doc-near" data-peer="${escAttr(n.event.id)}"
        title="A report ${distOf(n.m)} away whose occurrence falls in this marker's imagery window. Computed match, not a stored link — imagery cannot say which report made the damage. Click to open it; Link records that this marker documents it.">
        ◇ may document ${esc((n.event.name ?? 'event').slice(0, 48))} · ${
          dayOf(n.at)} · ${distOf(n.m)}
        <button type="button" class="mini doc-near-link"
          data-peer="${escAttr(n.event.id)}">Link</button></div>`).join('');
    box.innerHTML = rows + nearRows + (isMarker || isEvent
      ? `<button type="button" class="mini sel-doc-add">${isMarker
          ? '＋ Link to an event…' : '＋ Link a marker…'}</button>
         <div class="sel-doc-pick"></div>`
      : '');
    box.querySelectorAll<HTMLElement>('.sel-doc').forEach((r) =>
      r.addEventListener('click', () => hooks.onOpenPeer(r.dataset.peer!)));
    box.querySelectorAll<HTMLButtonElement>('.doc-x').forEach((b) =>
      b.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        try {
          await del(`/api/edges/${b.dataset.edge}`);
          await reloadCard();
        } catch { /* server said no (403/404); the card stands */ }
      }));
    box.querySelectorAll<HTMLButtonElement>('.doc-near-link').forEach((b) =>
      b.addEventListener('click', async (ev) => {
        ev.stopPropagation();   // Link authors the edge; the row click navigates
        try {
          await post('/api/edges',
                     { src: current.id, dst: b.dataset.peer, type: 'documents' });
          await reloadCard();
        } catch { /* server said no (403/409); the card stands */ }
      }));
    box.querySelector('.sel-doc-add')?.addEventListener('click', () =>
      void openDocPicker(current));
  }

  // One picker, two mouths: from a marker you attach EVENTS; from an event,
  // MARKERS. Either way the edge written is marker --documents--> event —
  // authorship surface changes, the direction (and therefore the authz bar:
  // write access to the marker) never does. The PRIMARY selection surface is
  // the map itself — a dropdown of a hundred rows all named "Damage marker"
  // is not a picker — with the nearest-first list as the fallback, and (from
  // an event) creating a brand-new linked marker in the same menu.
  function openDocPicker(current: EntityDto): void {
    const pick = host?.querySelector('.sel-doc-pick');
    if (!pick) return;
    const fromMarker = MARKER_TYPES.has(current.type);
    const noun = fromMarker ? 'event' : 'marker';

    const hint = (text: string): void => {
      const h = pick.querySelector('.sel-doc-hint');
      if (h) h.textContent = text;
    };
    let placeArmed: ((ev: maplibregl.MapMouseEvent) => void) | null = null;
    const disarm = (): void => {
      mapPick = null;
      map.getCanvas().style.cursor = '';
      document.removeEventListener('keydown', onEsc);
      if (placeArmed) {
        map.off('click', placeArmed);   // an Esc must also cancel placement
        placeArmed = null;
      }
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      disarm();
      hint('');
    };

    const linkTo = async (peerId: string): Promise<void> => {
      const body = fromMarker
        ? { src: current.id, dst: peerId, type: 'documents' }
        : { src: peerId, dst: current.id, type: 'documents' };
      await post('/api/edges', body);
      disarm();
      await reloadCard();
    };

    pick.innerHTML = `
      <div class="sel-doc-btns">
        <button type="button" class="mini" data-a="map">Pick on map</button>
        <button type="button" class="mini" data-a="list">From a list</button>
        ${fromMarker ? '' : `
          <button type="button" class="mini" data-a="new-damage">＋ Damage marker</button>
          <button type="button" class="mini" data-a="new-obs">＋ Observation</button>`}
        <button type="button" class="mini" data-a="cancel">Cancel</button>
      </div>
      <small class="sel-doc-hint"></small>
      <div class="sel-doc-list"></div>`;

    pick.querySelector('[data-a="cancel"]')!.addEventListener('click', () => {
      disarm();
      pick.innerHTML = '';
    });

    // -- pick an existing feature by clicking it ------------------------------
    pick.querySelector('[data-a="map"]')!.addEventListener('click', () => {
      hint(`Click a ${noun} on the map — Esc cancels.`);
      map.getCanvas().style.cursor = 'crosshair';
      document.addEventListener('keydown', onEsc);
      mapPick = {
        accept: (pid: string) => {
          void (async () => {
            try {
              const peer = cachedEntity(pid)
                ?? await get<EntityDto>(`/api/entities/${pid}`);
              const ok = fromMarker ? peer.type === 'live_event'
                : MARKER_TYPES.has(peer.type);
              if (!ok || pid === current.id) {
                hint(`That is not a ${noun} — still listening. Esc cancels.`);
                return;
              }
              await linkTo(pid);
            } catch {
              hint('Could not link (write access to the marker is required).');
            }
          })();
        },
      };
    });

    // -- the nearest-first list, as the fallback ------------------------------
    pick.querySelector('[data-a="list"]')!.addEventListener('click', () => {
      void (async () => {
        const list = pick.querySelector('.sel-doc-list')!;
        list.innerHTML = `<small>Finding nearby ${noun}s…</small>`;
        let candidates: EntityDto[] = [];
        try {
          const wanted = fromMarker ? 'live_event' : [...MARKER_TYPES].join(',');
          candidates = (await get<{ entities: EntityDto[] }>(
            `/api/entities?types=${wanted}&limit=500`)).entities;
        } catch {
          list.innerHTML = `<small>Could not load ${noun}s.</small>`;
          return;
        }
        const at = pointOfAny(current);
        const linked = new Set((current.edges ?? [])
          .filter((e) => e.type === 'documents').map((e) => e.peerId));
        const ranked = candidates
          .filter((c) => c.id !== current.id && !linked.has(c.id) && pointOfAny(c))
          .map((c) => ({ c, km: at ? kmBetween(at, pointOfAny(c)!) : 0 }))
          .sort((a, b) => a.km - b.km)
          .slice(0, 15);
        if (!ranked.length) {
          list.innerHTML = `<small>No ${noun}s available to link.</small>`;
          return;
        }
        const optLabel = (c: EntityDto, km: number): string => {
          const name = fromMarker
            ? String(c.attrs.place ?? c.name ?? 'event')
            : String(c.name ?? c.type);
          const when = fromMarker ? relTimeMs(whenOf(c).at)
            : String(c.attrs.date ?? relTimeMs(whenOf(c).at));
          return `${esc(name.slice(0, 40))} · ${esc(when)} · ${
            km.toFixed(km < 10 ? 1 : 0)} km`;
        };
        list.innerHTML = `
          <label class="ef"><span>${fromMarker ? 'Event' : 'Marker'} (nearest first)</span><select>
            ${ranked.map(({ c, km }) =>
              `<option value="${escAttr(c.id)}">${optLabel(c, km)}</option>`).join('')}
          </select></label>
          <button type="button" class="mini" data-a="link-picked">Link</button>`;
        const sel = list.querySelector('select') as HTMLSelectElement;
        list.querySelector('[data-a="link-picked"]')!.addEventListener('click', () => {
          linkTo(sel.value).catch(() =>
            hint('Could not link (write access to the marker is required).'));
        });
      })();
    });

    // -- create a NEW marker where you click, already linked ------------------
    // Dated by the EVENT's occurrence, not by today — markers age by
    // attrs.date (the Studio's rule), and a marker documenting last week's
    // flood belongs in last week's damage window.
    const armPlace = (markerType: string): void => {
      hint('Click the map where the marker goes — Esc cancels.');
      map.getCanvas().style.cursor = 'crosshair';
      document.addEventListener('keydown', onEsc);
      mapPick = { accept: () => { /* swallow feature clicks while placing */ } };
      const place = async (ev: maplibregl.MapMouseEvent): Promise<void> => {
        disarm();
        try {
          const when = whenOf(current).at;
          const created = await post<EntityDto>('/api/entities', {
            type: markerType,
            name: `${String(current.attrs.place ?? current.name ?? 'event')} — ${
              markerType === 'damage_marker' ? 'damage' : 'observation'}`,
            geometry: { type: 'Point',
                        coordinates: [ev.lngLat.lng, ev.lngLat.lat] },
            attrs: Number.isFinite(when)
              ? { date: new Date(when).toISOString().slice(0, 10) } : {},
          });
          cacheEntities([created]);
          await post('/api/edges', { src: created.id, dst: current.id,
                                     type: 'documents' });
          hooks.onEdited();
          // Land on the new marker so links and notes go on right away.
          await openSelection(map, created.id, hooks);
        } catch {
          hint('Could not create the marker.');
        }
      };
      placeArmed = (ev) => void place(ev);
      map.once('click', placeArmed);
    };
    pick.querySelector('[data-a="new-damage"]')
      ?.addEventListener('click', () => armPlace('damage_marker'));
    pick.querySelector('[data-a="new-obs"]')
      ?.addEventListener('click', () => armPlace('observation'));
  }

  // Which story this object is filed under. Filing NARROWS who can see it (a
  // tenant story is invisible outside its org); unfiling WIDENS it back to
  // everyone. That is the opposite of the intuitive reading — "remove from the
  // story" sounds like hiding — so the card says it in words here and again in
  // the confirm. The line paints from the entity in hand; the curator control
  // grows in after the /api/me probe answers, so a non-curator's card never
  // flashes an affordance they cannot use.
  function paintStory(): void {
    const box = host?.querySelector('#sel-story');
    if (!box) return;
    const filedId = entity.storyId ?? null;
    // Not every filed story is listed: /api/stories omits archived ones, and
    // archiving is what DELETE does — the objects stay filed and stay visible.
    const current = getStory(filedId);
    if (!filedId && !getStories().length) return;   // no stories at all: no row
    box.innerHTML = `<dl><dt>filed under</dt><dd>${
      current ? esc(current.name)
        : filedId ? 'a story that is not in your list'
          : 'nothing — visible in every story'}</dd></dl>`;
    void isCurator().then((ok) => {
      // The card may have been closed or replaced during the probe.
      if (!ok || host?.dataset.entityId !== entity.id) return;
      const listed = orderedStories();
      // No-break spaces for the tree indent: a <select> collapses ordinary
      // leading whitespace, so the depth would not read at all.
      const options = listed.map(({ story, depth }) =>
        `<option value="${escAttr(story.id)}"${story.id === filedId ? ' selected' : ''}
          >${'\u00a0\u00a0'.repeat(depth)}${esc(story.name)}</option>`).join('');
      // Carry an unlisted current story as its own option. Without it the
      // control reads "Unfiled" for something that is filed, and the next
      // change silently unfiles it — which WIDENS who can see it.
      const unlisted = filedId && !listed.some((n) => n.story.id === filedId)
        ? `<option value="${escAttr(filedId)}" selected>${
          current ? esc(current.name) : 'Current story'} (not listed)</option>`
        : '';
      box.innerHTML = `<div class="sel-edit">
        <label class="ef"><span>Filed under</span>
          <select>
            <option value=""${filedId ? '' : ' selected'}>Unfiled — every story</option>
            ${unlisted}${options}
          </select>
          <small>Filing narrows who can see this object. Unfiling makes it
            visible in every story — more visible, not less.</small>
        </label></div>`;
      const sel = box.querySelector('select') as HTMLSelectElement;
      sel.addEventListener('change', () => void moveStory(sel));
    });
  }

  async function moveStory(sel: HTMLSelectElement): Promise<void> {
    const target = sel.value || null;
    const before = entity.storyId ?? null;
    if (target === before) return;
    if (!target && !confirm(`Unfile "${entity.name ?? entity.type}"?\n\n`
      + 'It stops belonging to any story and becomes visible in ALL of them. '
      + 'This widens who can see it; it does not hide it.')) {
      sel.value = before ?? '';
      return;
    }
    sel.disabled = true;
    try {
      // An explicit null is the unfile; omitting the key would carry the
      // current story forward (backend/app/entities/service.py).
      await put(`/api/entities/${entity.id}`, { story: target });
      hooks.onEdited();
      await openSelection(map, entity.id, hooks);
    } catch (err) {
      sel.disabled = false;
      sel.value = before ?? '';
      alert(err instanceof Error ? err.message : 'Could not move the object.');
    }
  }

  host.querySelector('#sel-zoom')!.addEventListener('click', () => {
    if (!entity.geometry) return;
    if (entity.geometry.type === 'Point') {
      map.flyTo({ center: entity.geometry.coordinates as [number, number], zoom: 13 });
    } else {
      const coords = JSON.stringify(entity.geometry).match(/-?\d+\.?\d*/g)!.map(Number);
      const lons = coords.filter((_, i) => i % 2 === 0);
      const lats = coords.filter((_, i) => i % 2 === 1);
      map.fitBounds([[Math.min(...lons), Math.min(...lats)],
                     [Math.max(...lons), Math.max(...lats)]], { padding: 80 });
    }
  });

  host.querySelector('#sel-scan')?.addEventListener('click', () => {
    hooks.onScanHere(entity);
  });
  // A territory's primary action is moving a district, not scanning imagery
  // of a whole country. The flow lives in territory.ts (the Layers legend's
  // "Reassign district"); the card just starts it.
  host.querySelector('#sel-reassign')?.addEventListener('click', () => {
    closeSelection(map);
    startReassignFromCard();
  });
  // Contest → capture: the form is the legend's (territory.ts); the card
  // hands it the territory and closes.
  host.querySelector('#sel-resolve')?.addEventListener('click', () => {
    closeSelection(map);
    startResolveFromCard(entity);
  });
  // The drag is curation (require_curator on the server); the button grows
  // in after the /api/me probe answers, like the story control.
  const dragBtn = host.querySelector('#sel-drag') as HTMLButtonElement | null;
  const cutBtn = host.querySelector('#sel-cut') as HTMLButtonElement | null;
  const areaBtn = host.querySelector('#sel-area') as HTMLButtonElement | null;
  const claimBtn = host.querySelector('#sel-claim') as HTMLButtonElement | null;
  if (dragBtn && hooks.onDragBoundary) {
    void isCurator().then((ok) => {
      if (!ok || host?.dataset.entityId !== entity.id) return;
      dragBtn.hidden = false;
      if (cutBtn && hooks.onCutBoundary) cutBtn.hidden = false;
      if (areaBtn && hooks.onMarkArea) areaBtn.hidden = false;
      if (claimBtn && hooks.onAddGround) claimBtn.hidden = false;
    });
    claimBtn?.addEventListener('click', () => {
      closeSelection(map);
      hooks.onAddGround?.(entity);
    });
    areaBtn?.addEventListener('click', () => {
      closeSelection(map);
      hooks.onMarkArea?.(entity);
    });
    dragBtn.addEventListener('click', () => {
      closeSelection(map);
      hooks.onDragBoundary!(entity);
    });
    cutBtn?.addEventListener('click', () => {
      closeSelection(map);
      hooks.onCutBoundary?.(entity);
    });
  }

  host.querySelector('#sel-delete')!.addEventListener('click', async () => {
    if (!confirm(`Delete "${entity.name ?? entity.type}"? History is kept.`)) return;
    await del(`/api/entities/${entity.id}`);
    closeSelection(map);
    hooks.onDeleted();
  });

  host.querySelector('#sel-history-btn')!.addEventListener('click', async () => {
    const target = host!.querySelector('#sel-history')!;
    if (target.childElementCount) { target.innerHTML = ''; return; }
    const { versions } = await get<{ versions: HistoryVersion[] }>(
      `/api/entities/${entity.id}/history`);
    target.innerHTML = '<dl>' + versions.slice(0, 12).map((v) => {
      const when = v.validFrom.slice(0, 16).replace('T', ' ');
      const who = v.byRule ? 'rule' : (v.createdBy ?? 'system');
      return `<dt>${when}</dt><dd>${v.changeKind} · ${who}${v.validTo ? '' : ' · current'}</dd>`;
    }).join('') + '</dl>';
  });

  // --- Edit: retype, rename, edit attributes, and redraw/move the geometry.
  // Every save is a PUT that opens a new version (shows in History), and the
  // card reopens on the fresh data.
  type SchemaProp = { type?: string; title?: string; enum?: unknown[]; default?: unknown };
  interface FormState { name: string; unconfirmed: boolean; attrs: Record<string, unknown>; }

  // The current geometry's coarse kind — decides which types can host it (so a
  // retype never lands an invalid geometry) and which redraw buttons make sense.
  const currentGeomKind = ((): string => {
    const g = entity.geometry?.type;
    if (!g) return 'none';
    if (g === 'Point' || g === 'MultiPoint') return 'point';
    if (g === 'LineString' || g === 'MultiLineString') return 'line';
    return 'polygon';
  })();

  // Parse a type's stored JSON Schema: its editable fields, and whether it is
  // strict (`additionalProperties:false`) — a strict target can't hold attrs it
  // doesn't declare, so a retype into one must shed the stragglers.
  const schemaOf = (typeKey: string): { props: Record<string, SchemaProp>; strict: boolean } => {
    const schema = registry?.types.find((t) => t.key === typeKey)?.attrSchema as
      { properties?: Record<string, SchemaProp>; additionalProperties?: unknown } | undefined;
    return {
      props: (schema?.properties ?? {}) as Record<string, SchemaProp>,
      strict: schema?.additionalProperties === false,
    };
  };

  // Types this entity could become: same geometry kind (backend enforces it),
  // grouped by domain, current type always present. Geometry conversion is a
  // separate action, so the picker stays within what the current shape allows.
  function typeSelectHtml(selectedKey: string): string {
    const domainOf = new Map((registry?.categories ?? []).map((c) => [c.key, c.domain]));
    const compatible = (registry?.types ?? [])
      .filter((t) => t.geometryKinds.includes(currentGeomKind) || t.key === selectedKey)
      .slice().sort((a, b) => a.sort - b.sort);
    const groups = Object.keys(DOMAIN_LABEL).map((domain) => {
      const opts = compatible
        .filter((t) => (domainOf.get(t.category) ?? 'object') === domain)
        .map((t) => `<option value="${escAttr(t.key)}"` +
          `${t.key === selectedKey ? ' selected' : ''}>${esc(t.label)}</option>`).join('');
      return opts ? `<optgroup label="${esc(DOMAIN_LABEL[domain]!)}">${opts}</optgroup>` : '';
    }).join('');
    return `<label class="ef"><span>Type</span>` +
      `<select id="sel-type">${groups || `<option selected>${esc(selectedKey)}</option>`}</select></label>`;
  }

  // Closed vocabularies the CLIENT knows but the stored JSON Schema does not
  // declare. `live_event`'s attr_schema is deliberately `{"type":"object"}` —
  // it holds the whole extractor payload — so `kind` would otherwise render as
  // a free-text box, where a typo produces an event the map has no glyph for,
  // the feed labels "Unclear", and dedupe cannot family-match. The vocabulary
  // is LIVE_KINDS, the same one the map and feed read, so a correction here
  // can only ever produce a kind the rest of the client understands.
  const CLIENT_ENUMS: Record<string, Record<string, string[]>> = {
    live_event: { kind: Object.keys(LIVE_KINDS) },
  };

  /** {value,label} options for a client-known vocabulary, or null. The current
   *  value is always included even when unknown — an editor that silently
   *  rewrites a field the operator did not touch is worse than an odd row. */
  function clientEnumOptions(typeKey: string, key: string, current: string):
      { value: string; label: string }[] | null {
    const vocab = CLIENT_ENUMS[typeKey]?.[key];
    if (!vocab) return null;
    const values = vocab.includes(current) || !current ? vocab : [current, ...vocab];
    return values.map((v) => ({
      value: v,
      label: LIVE_KINDS[v] ? `${LIVE_KINDS[v]!.glyph} ${LIVE_KINDS[v]!.label}` : v,
    }));
  }

  function fieldRow(key: string, schema: SchemaProp | undefined, value: unknown,
                    typeKey: string): string {
    const label = esc(String(schema?.title ?? key));
    const v = value ?? schema?.default ?? '';
    const known = clientEnumOptions(typeKey, key, String(v));
    if (known && !schema?.enum) {
      const opts = known.map((o) =>
        `<option value="${escAttr(o.value)}"${o.value === String(v)
          ? ' selected' : ''}>${esc(o.label)}</option>`).join('');
      return `<label class="ef"><span>${label}</span>` +
        `<select data-attr="${escAttr(key)}">${opts}</select></label>`;
    }
    if (schema?.type === 'array') {
      // One item per line — the damage report's links (036) and any later
      // array-of-string attr a schema declares. The generic text input would
      // comma-join the array and hand the schema a string, which 422s.
      const items = Array.isArray(value) ? value.map(String) : [];
      return `<label class="ef"><span>${label}</span>` +
        `<textarea data-attr-lines="${escAttr(key)}" rows="3" ` +
        `placeholder="one URL per line">${escAttr(items.join('\n'))}</textarea></label>`;
    }
    if (schema?.enum) {
      const opts = schema.enum.map((o) =>
        `<option value="${escAttr(String(o))}"${String(o) === String(v)
          ? ' selected' : ''}>${esc(String(o))}</option>`).join('');
      return `<label class="ef"><span>${label}</span>` +
        `<select data-attr="${escAttr(key)}">${opts}</select></label>`;
    }
    if (schema?.type === 'boolean') {
      return `<label class="ef ef-check"><input type="checkbox" ` +
        `data-attr="${escAttr(key)}"${v ? ' checked' : ''} /><span>${label}</span></label>`;
    }
    const inputType = schema?.type === 'number' || schema?.type === 'integer'
      ? 'number' : 'text';
    return `<label class="ef"><span>${label}</span><input type="${inputType}" ` +
      `data-attr="${escAttr(key)}" value="${escAttr(String(v))}" /></label>`;
  }

  // Redraw buttons for every drawable geometry kind the *current* type permits —
  // "Draw area outline" on a point base, "Set point location" on an area, etc.
  // (Geometry edits save immediately and reopen the card, so they act on the
  // entity as it stands, not on an unsaved retype.)
  function geomButtons(): string {
    const kinds = registry?.types.find((t) => t.key === entity.type)?.geometryKinds ?? [];
    const btns: string[] = [];
    if (kinds.includes('point')) {
      btns.push(`<button type="button" data-geom="point">${
        currentGeomKind === 'point' ? 'Move point' : 'Set point location'}</button>`);
    }
    if (kinds.includes('polygon')) {
      btns.push(`<button type="button" data-geom="polygon">${
        currentGeomKind === 'polygon' ? 'Redraw boundary' : 'Draw area outline'}</button>`);
    }
    return btns.length ? `<div class="sel-edit-geom">${btns.join('')}
      <small>Opens the map to reshape; other edits save separately.</small></div>` : '';
  }

  // Read the live form back into a state object so an in-progress edit survives
  // a retype (matching keys carry over; the rest is dropped by the new schema).
  function collectForm(): FormState {
    const form = host!.querySelector('#sel-edit-form') as HTMLFormElement;
    const name = (form.querySelector('input[name="__name"]') as HTMLInputElement).value;
    const unconfirmed = (form.querySelector('[data-flag="unconfirmed"]') as
      HTMLInputElement | null)?.checked ?? false;
    const attrs: Record<string, unknown> = { ...entity.attrs };
    form.querySelectorAll<HTMLElement>('[data-attr]').forEach((el) => {
      const key = el.dataset.attr!;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') { attrs[key] = el.checked; return; }
      const raw = (el as HTMLInputElement | HTMLSelectElement).value;
      if (raw === '') { delete attrs[key]; return; }
      attrs[key] = raw;
    });
    form.querySelectorAll<HTMLTextAreaElement>('[data-attr-lines]').forEach((el) => {
      const key = el.dataset.attrLines!;
      const items = el.value.split('\n').map((s) => s.trim()).filter(Boolean);
      if (items.length) attrs[key] = items; else delete attrs[key];
    });
    return { name, unconfirmed, attrs };
  }

  function renderForm(typeKey: string, vals: FormState): void {
    const { props, strict } = schemaOf(typeKey);
    const keys = Object.keys(props);
    // A permissive schema also shows any free-form string/number attrs carried
    // on the row; a strict one shows only what it declares (the rest won't save).
    if (!strict) {
      for (const [k, v] of Object.entries(vals.attrs)) {
        if (keys.includes(k) || HIDDEN_ATTRS.has(k) || EDIT_SYSTEM.has(k)) continue;
        if (typeof v === 'string' || typeof v === 'number') keys.push(k);
      }
    }
    const body = host!.querySelector('.body')!;
    body.innerHTML = `
      <form id="sel-edit-form" class="sel-edit">
        <label class="ef"><span>Name</span>
          <input name="__name" value="${escAttr(vals.name)}" /></label>
        ${typeSelectHtml(typeKey)}
        <label class="ef ef-check"><input type="checkbox" data-flag="unconfirmed"
          ${vals.unconfirmed ? 'checked' : ''} /><span>Unconfirmed</span></label>
        ${keys.map((k) => fieldRow(k, props[k], vals.attrs[k], typeKey)).join('')}
        ${geomButtons()}
      </form>`;
    const form = body.querySelector('#sel-edit-form')!;
    form.querySelector('#sel-type')?.addEventListener('change', () =>
      renderForm((form.querySelector('#sel-type') as HTMLSelectElement).value, collectForm()));
    body.querySelectorAll<HTMLButtonElement>('[data-geom]').forEach((btn) =>
      btn.addEventListener('click', () =>
        hooks.onEditGeometry(entity, btn.dataset.geom as GeometryEditKind)));
  }

  // Set by the rotate grip while the editor is open; committed by Save (either
  // through the form's bearing_deg field, or directly when the form lacks one).
  let draggedBearing: number | null = null;

  function renderEditor(): void {
    renderForm(entity.type, {
      name: entity.name ?? '',
      unconfirmed: !!entity.attrs.unconfirmed,
      attrs: { ...entity.attrs },
    });
    // Directional systems (a narrowed sector) get an on-map rotate grip while
    // editing: drag to re-aim, and the new bearing lands in the form so Save
    // commits it alongside the other edits. Full-circle systems and non-point
    // geometry get no handle; Save/Cancel reopen the card, which hides it.
    const shape = radarShapeOf(entity);
    if (entity.geometry?.type === 'Point' && shape.azimuthDeg < 360) {
      showRotateHandle(map, entity.geometry.coordinates as [number, number],
        draggedBearing ?? shape.bearingDeg, (bearing) => {
          draggedBearing = Math.round(bearing);
          const field = host?.querySelector<HTMLInputElement>(
            '#sel-edit-form [data-attr="bearing_deg"]');
          if (field) field.value = String(draggedBearing);
        });
    }
    const actions = host!.querySelector('.actions')!;
    actions.innerHTML =
      '<button class="primary" id="sel-save">Save</button>' +
      '<button id="sel-cancel">Cancel</button>';
    actions.querySelector('#sel-cancel')!.addEventListener('click',
      () => void openSelection(map, entity.id, hooks));
    actions.querySelector('#sel-save')!.addEventListener('click', saveEdits);
  }

  async function saveEdits(): Promise<void> {
    const form = host!.querySelector('#sel-edit-form') as HTMLFormElement;
    const targetType = (form.querySelector('#sel-type') as HTMLSelectElement | null)?.value
      ?? entity.type;
    const name = (form.querySelector('input[name="__name"]') as HTMLInputElement)
      .value.trim();
    const { props, strict } = schemaOf(targetType);
    let attrs: Record<string, unknown> = { ...entity.attrs };
    // Retype into a strict schema: keep only attrs it declares, plus the
    // universal flags that bypass per-type validation server-side.
    if (targetType !== entity.type && strict) {
      attrs = Object.fromEntries(Object.entries(attrs).filter(
        ([k]) => k in props || RESERVED_FLAGS.has(k)));
    }
    const unconfirmed = form.querySelector('[data-flag="unconfirmed"]') as HTMLInputElement | null;
    if (unconfirmed?.checked) attrs.unconfirmed = true;
    else delete attrs.unconfirmed;
    form.querySelectorAll<HTMLElement>('[data-attr]').forEach((el) => {
      const key = el.dataset.attr!;
      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        attrs[key] = el.checked; return;
      }
      const raw = (el as HTMLInputElement | HTMLSelectElement).value;
      if (raw === '') { delete attrs[key]; return; }
      const t = props[key]?.type;
      if (t === 'number' || t === 'integer') {
        const n = Number(raw);
        attrs[key] = Number.isNaN(n) ? raw : n;
      } else {
        attrs[key] = raw;
      }
    });
    // A rotate-grip drag still commits when the form has no bearing_deg field
    // (e.g. the bearing comes from the type default, not an entity attr).
    if (draggedBearing !== null && !form.querySelector('[data-attr="bearing_deg"]')) {
      attrs.bearing_deg = draggedBearing;
    }
    try {
      const patch: Record<string, unknown> = { name: name || null, attrs };
      if (targetType !== entity.type) patch.type = targetType;
      await put(`/api/entities/${entity.id}`, patch);
      hooks.onEdited();
      await openSelection(map, entity.id, hooks);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save the changes.');
    }
  }

  host.querySelector('#sel-edit')!.addEventListener('click', renderEditor);
}
